"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <nav className="flex items-center gap-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 animate-pulse rounded bg-muted" />)}
      </nav>;
  }
  return <nav className="flex items-center gap-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="group relative inline-block font-sans text-sm font-semibold tracking-tight text-foreground transition-colors hover:text-accent">
            {item.label}
            <span className="absolute left-0 top-1/2 h-[2px] w-full origin-left -translate-y-1/2 scale-x-0 bg-accent transition-transform duration-300 group-hover:scale-x-100" />
          </Link>
        </div>)}
    </nav>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div data-section-id="122425" className="fixed inset-0 z-[100] flex flex-col bg-background/98 px-6 pt-24 backdrop-blur-md md:hidden">
      <Button onClick={onClose} variant="ghost" size="icon" className="absolute right-6 top-6" aria-label="Close menu">
        <X className="size-8 text-foreground" />
      </Button>

      <div className="flex flex-col gap-6">
        {loading ? [1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-32 animate-pulse rounded bg-muted" />) : menuItems.map((item, i) => <div key={item.id} data-index={i}>
              <Link to={item.href || "#"} onClick={onClose} className="group relative inline-block font-sans text-2xl font-bold tracking-tight text-foreground transition-colors hover:text-accent">
                {item.label}
                <span className="absolute left-0 top-1/2 h-[2px] w-full origin-left -translate-y-1/2 scale-x-0 bg-accent transition-transform duration-300 group-hover:scale-x-100" />
              </Link>
            </div>)}
        <div className="mt-8">
          <Button className="h-12 w-full rounded-[4px] bg-accent font-sans text-lg font-bold text-accent-foreground shadow-lg transition-all hover:bg-primary hover:text-primary-foreground">
            Start Your Quote
          </Button>
        </div>
      </div>
    </div>;
}
export default function MainHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <header id="header" className={`sticky top-0 z-50 w-full border-b border-border transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md" : "bg-background"}`}>
        <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
          <Link to="/HomePage" className="flex items-center">
            <img src="https://climbing-camel.10web.cloud/wp-content/uploads/2026/03/Untitled-design-13.png" alt="Caravan Smash Repairs Logo" className="h-12" />
          </Link>

          <div className="hidden md:flex md:items-center">
            <MenuProvider menu_id="44">
              <DesktopNav />
            </MenuProvider>
          </div>

          <div className="hidden md:block">
            <Button className="rounded-[4px] bg-accent font-sans font-bold text-accent-foreground shadow-md transition-all hover:bg-primary hover:text-primary-foreground">
              Start Your Quote
            </Button>
          </div>

          <Button variant="ghost" size="icon" className="md:hidden text-foreground" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
            <Menu className="size-6" />
          </Button>
        </div>
      </header>

      <MenuProvider menu_id="44">
        <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </>;
}